/**
 * Hops — alpha/beta bands, oil chemistry, substitutions.
 *
 * Numbers follow grower/broker published specs (Yakima Chief, Hopsteiner,
 * BarthHaas, Crosby, NZ Hops, HPA) as of our knowledge snapshot, expressed as
 * ranges because every figure moves with crop year.
 */

import { z } from 'zod';
import {
  descriptionSchema,
  originSchema,
  productUrlSchema,
  provenanceSchema,
  range,
  slugSchema,
  tagsSchema,
} from './common';

/**
 * How well a variety holds its alpha acids in storage.
 *
 * One published figure and the page it was read off. It carries its own source
 * rather than leaning on the entry's `url` because these numbers do not come
 * from the grower's product page: the growers do not publish them. Four sources
 * between them cover the varieties that have a figure at all — a 1994 *Brewing
 * Techniques* table, the Hop Growers of America's 2016 variety manual (which
 * dropped the column afterwards), a handful of US plant patents, and nothing
 * else. `core/hop-freshness.ts` is the curve that reads it.
 *
 * **Most hops have no entry here and that is correct.** Hopsteiner, BarthHaas,
 * Crosby, Charles Faram and Yakima Valley Hops were all checked in September
 * 2026 and none of them publishes a storage-stability figure per variety; every
 * modern American and New Zealand variety is therefore blank rather than
 * guessed. Aggregator sites carry numbers for far more of them, and disagree
 * with the published sources on half the varieties where both have one, which
 * is the reason none of those numbers is here.
 */
export const hopStorageStabilitySchema = z.strictObject({
  /**
   * Alpha acids lost after **six months at 20 °C with no barrier packaging**, %.
   *
   * Deliberately *not* called `hsi`, which is what Brewfather and BeerSmith call
   * the same field. "Hop storage index" properly means the spectrophotometric
   * A275/A325 ratio of one physical lot — about 0.25 fresh, climbing as it
   * oxidises — which is a measurement of a sample rather than a property of a
   * variety. The two are routinely confused; a value in this field is a variety's
   * decay *rate*, and sources that publish it the other way up as "% alpha
   * remaining" are converted on the way in.
   */
  alphaLostPercentSixMonths: z.number().min(0).max(100),
  /** The published page or document the figure was read from. */
  source: productUrlSchema,
});

/** @see hopStorageStabilitySchema */
export type HopStorageStability = z.infer<typeof hopStorageStabilitySchema>;

/** What the variety is normally bought for. Dual means it earns both jobs. */
export const hopPurposeSchema = z.enum(['bittering', 'aroma', 'dual']);

/** @see hopPurposeSchema */
export type HopPurpose = z.infer<typeof hopPurposeSchema>;

/**
 * The four oil fractions worth modelling.
 *
 * Each is a percentage of total oil. `null` where the grower publishes no
 * breakdown — an all-`null` object is preferable to invented chemistry.
 */
export const hopOilCompositionSchema = z.strictObject({
  /** Myrcene — resinous/citrus, the dominant fraction in most US varieties. */
  myrcenePercent: z.number().min(0).max(90).nullable(),
  /** Humulene — noble/woody/herbal. */
  humulenePercent: z.number().min(0).max(70).nullable(),
  /** Caryophyllene — peppery/spicy. */
  caryophyllenePercent: z.number().min(0).max(30).nullable(),
  /** Farnesene — floral/green apple; near zero outside a few varieties. */
  farnesenePercent: z.number().min(0).max(30).nullable(),
});

/** @see hopOilCompositionSchema */
export type HopOilComposition = z.infer<typeof hopOilCompositionSchema>;

/**
 * Same-genotype lots grown in different places share this slug.
 *
 * The canonical landrace uses its own id (`saaz`, `cascade`). Origin variants
 * reuse that id — Michigan-grown Saaz has `cultivar: 'saaz'`, Taiheke has
 * `cultivar: 'cascade'`. Related-but-different plants (Sterling, Motueka,
 * Saaz Late, US Tettnanger) stay `null`: they are Saaz-type or Saaz-derived,
 * not Saaz. `null` also means the variety has no other origin lot in this
 * catalog.
 */
export const hopCultivarSchema = slugSchema.nullable();

/** @see hopCultivarSchema */
export type HopCultivar = z.infer<typeof hopCultivarSchema>;

/**
 * Growing district when we know it more finely than {@link hopSchema}'s
 * country `origin` — Žatec, Hallertau, Yakima Valley, Michigan.
 *
 * `null` when only the country is known. Never a guess: if the sheet says
 * "United States" and not which valley, this stays `null`.
 */
export const hopGrowingRegionSchema = z.string().min(2).max(40).nullable();

/** @see hopGrowingRegionSchema */
export type HopGrowingRegion = z.infer<typeof hopGrowingRegionSchema>;

/**
 * One hop variety, one origin lot of a variety, or a vendor blend sold as a
 * single product.
 *
 * The third kind is the newest and the least like the others: a blend has no
 * genotype, so `cultivar` is `null` and the substitutes graph treats it as a
 * leaf. Its analysis is real but it is one seller's lot rather than a
 * specification — the recipe can change between crops without the name
 * changing — so a blend carries `provenance: 'representative'` and says in its
 * own description what it is made of and that the proportions are the
 * vendor's.
 */
/**
 * What sort of row this is.
 *
 * Absent means `variety`, so the 197 rows written before this existed need no
 * edit and the object stays strict. The discriminator earns its place in
 * `hops.test.ts`: a `product` is the one kind that may have no alpha, no beta
 * and no cone oil, because a terpene dosing liquid genuinely has none of them
 * and `{ min: 0, max: 0 }` says so honestly.
 */
export const hopKindSchema = z.enum(['variety', 'blend', 'product']);

/** @see hopKindSchema */
export type HopKind = z.infer<typeof hopKindSchema>;

export const hopSchema = z.strictObject({
  /** Stable slug, e.g. `citra`, `hallertauer-mittelfrueh`, `saaz-michigan`. */
  id: slugSchema,
  /** Variety name as the trade uses it. Origin lots put the place in parentheses. */
  name: z.string().min(2).max(60),
  /** Country of origin / where this lot is grown commercially. */
  origin: originSchema,
  /** @see hopGrowingRegionSchema */
  growingRegion: hopGrowingRegionSchema,
  /** @see hopCultivarSchema */
  cultivar: hopCultivarSchema,
  /** @see hopPurposeSchema */
  purpose: hopPurposeSchema,
  /**
   * Alpha acid, % w/w. The bittering figure.
   *
   * Bounded at 100 rather than at the ~30 a cone can reach, because a CO₂
   * extract runs 50-65 % and is a legitimate row here. The cone ceiling is
   * still enforced — in `hops.test.ts`, against rows that grew on a bine,
   * where it is a real check rather than an accident of what else is in the
   * file.
   */
  alphaAcidPercent: range(0, 100),
  /** Beta acid, % w/w. Bounded like alpha, and for the same reason. */
  betaAcidPercent: range(0, 100),
  /** Cohumulone as % *of alpha acid*. Lower is usually softer bitterness. */
  cohumulonePercentOfAlpha: range(0, 70).nullable(),
  /** Total oil, mL per 100 g. Aroma potential in one number. */
  totalOilMlPer100g: range(0, 6),
  /** @see hopOilCompositionSchema */
  oilComposition: hopOilCompositionSchema.nullable(),
  /**
   * @see hopStorageStabilitySchema
   *
   * Optional rather than nullable, and it is the only field on a hop that is:
   * a `null` here would have to be written on ~150 entries to say "nobody
   * publishes this", which is the default state of the world for this figure
   * rather than a fact about any one variety.
   */
  storageStability: hopStorageStabilitySchema.optional(),
  /**
   * Other hop {@link hopSchema} ids that stand in for this one.
   *
   * Every id must resolve inside this catalog — the test suite enforces it, so
   * the editor can render substitutes as links without defensive checks.
   */
  substitutes: z.array(slugSchema).max(8),
  /** Descriptor tags used by the type-ahead: `citrus`, `dank`, `noble`. */
  flavorTags: tagsSchema,
  /** Our own words. */
  description: descriptionSchema,
  /** General-purpose search tags (region, breeding programme, aliases). */
  tags: tagsSchema,
  /** @see hopKindSchema. Absent means `variety`. */
  kind: hopKindSchema.optional(),
  /**
   * The form a recipe addition should take when this row is added, when
   * `pellet` would be wrong.
   *
   * A variety is bought as pellets and `snapshotHop` defaults to that. A
   * product is not: adding a CO2 extract as a pellet would run it through the
   * wrong utilisation, and adding a pre-isomerised extract as one would
   * under-report its bitterness fourfold. The catalog is the only place that
   * knows which a row is, so it is the place that says.
   */
  defaultForm: z
    .enum(['whole', 'plug', 'pellet', 'cryo', 'co2-extract', 'iso-extract', 'aroma-extract'])
    .optional(),
  /**
   * Iso-alpha-acid content, **% w/w**, for pre-isomerised products.
   *
   * Kept apart from {@link hopSchema.shape.alphaAcidPercent} for the reason
   * `recipeHopSchema` keeps them apart: they are different molecules and
   * reading one as the other mis-bitters a beer silently.
   */
  isoAlphaPercent: range(0, 100).optional(),
  /** @see provenanceSchema */
  /** @see productUrlSchema */
  url: productUrlSchema.optional(),
  provenance: provenanceSchema,
});

/** @see hopSchema */
export type Hop = z.infer<typeof hopSchema>;
